/**
 * List pages для Підпорції 1.4 (2026-05-25).
 * Editor + print-view UI — TECHDEBT (окрема міні-порція).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import {
  inventory,
  sale,
  type FixedAssetInventory,
  type FixedAssetSale,
} from '@/api/asset_inventory_sale';


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function InventoryPage() {
  const list = useQuery({
    queryKey: ['fa-inventory'],
    queryFn: () => inventory.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<FixedAssetInventory>[]>(
    () => [
      {
        key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'location_kind', header: 'Скоуп', width: 130,
        sortValue: (r) => r.location_kind ?? '',
        cell: (r) => r.location_kind,
      },
      {
        key: 'inventory_period_start', header: 'Період з', width: 130,
        sortValue: (r) => r.inventory_period_start ?? '',
        cell: (r) => r.inventory_period_start,
      },
      {
        key: 'inventory_period_end', header: 'Період до', width: 130,
        sortValue: (r) => r.inventory_period_end ?? '',
        cell: (r) => r.inventory_period_end,
      },
      {
        key: 'items', header: 'Позицій', width: 110, align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Iнвентаризація ОЗ</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<FixedAssetInventory>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        onRowDoubleClick={(r) => {
          if (r.id && r.posted) {
            window.open(inventory.printViewUrl(r.id), '_blank');
          }
        }}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function SalePage() {
  const list = useQuery({
    queryKey: ['fa-sale'],
    queryFn: () => sale.list({ limit: 200 }),
  });
  const buyerLabel = (r: FixedAssetSale) =>
    r.counterparty_id
      ? `Контрагент ${r.counterparty_id.slice(0, 8)}…`
      : r.external_buyer_name ?? '—';
  const saleTotal = (r: FixedAssetSale) =>
    (r.items ?? [])
      .map((i) => Number(i.sale_price_with_vat ?? 0))
      .reduce((a, b) => a + b, 0);
  const cols = useMemo<ColumnDef<FixedAssetSale>[]>(
    () => [
      {
        key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'buyer', header: 'Покупець',
        sortValue: (r) => buyerLabel(r),
        cell: (r) => buyerLabel(r),
      },
      {
        key: 'items', header: 'Позицій', width: 110, align: 'right',
        sortValue: (r) => r.items?.length ?? 0,
        cell: (r) => r.items?.length ?? 0,
      },
      {
        key: 'total_with_vat', header: 'Сума з ПДВ', width: 150, align: 'right',
        sortValue: (r) => saleTotal(r),
        cell: (r) => fmt2(saleTotal(r)),
      },
      {
        key: 'posted', header: 'Проведено', width: 120,
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Продаж ОЗ</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<FixedAssetSale>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        onRowDoubleClick={(r) => {
          if (r.id && r.posted) {
            window.open(sale.printViewUrl(r.id), '_blank');
          }
        }}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
